import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card, CardTitle } from '@shared/ui/Card';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { Input } from '@shared/ui/Input';
import {
  useConnectKadrySourceMutation,
  useDisconnectKadrySourceMutation,
  useDiscoverKadrySourceMutation,
  useListKadrySecretsQuery,
  useListKadrySourcesQuery,
  useSetKadrySecretMutation,
} from '@shared/api';
import { kadryDateTime } from '@entities/kadry';
import { KadryCardHead, KadryErrorBanner, KadryMuted } from '../ui';
import {
  CardBody,
  Code,
  hostOf,
  KeyState,
  Line,
  Row,
  SOURCE_UI,
  Step,
  type KadrySourceId,
} from './connectionsShared';
import { TariffsQuotasCard } from './TariffsQuotasCard';

export function SourceConnectionsCard({ source }: { source: KadrySourceId }) {
  const ui = SOURCE_UI[source];
  const [params] = useSearchParams();
  const { data: sources, error, refetch } = useListKadrySourcesQuery();
  const { data: secrets } = useListKadrySecretsQuery();
  const [setSecret, secretState] = useSetKadrySecretMutation();
  const [connect, connectState] = useConnectKadrySourceMutation();
  const [discover, discoverState] = useDiscoverKadrySourceMutation();
  const [disconnect, disconnectState] = useDisconnectKadrySourceMutation();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saved, setSaved] = useState(false);
  const acc = sources?.find((s) => s.source === source);
  const idMeta = secrets?.find((s) => s.name === ui.idSecret);
  const secretMeta = secrets?.find((s) => s.name === ui.secretSecret);
  const result = params.get(source);
  const target = ui.domainOnly ? hostOf(acc?.redirect_uri) : acc?.redirect_uri;

  async function saveApp(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    try {
      if (clientId.trim()) {
        await setSecret({ name: ui.idSecret, kind: 'api_token', value: clientId.trim() }).unwrap();
      }
      if (clientSecret.trim()) {
        await setSecret({ name: ui.secretSecret, kind: 'api_token', value: clientSecret.trim() }).unwrap();
      }
      setClientId('');
      setClientSecret('');
      setSaved(true);
    } catch {
      /* ошибка показана ниже */
    }
  }

  async function onConnect() {
    try {
      const r = await connect(source).unwrap();
      window.location.assign(r.authorize_url);
    } catch {
      /* ошибка показана ниже */
    }
  }

  return (
    <Card>
      <KadryCardHead>
        <CardTitle>{ui.title}</CardTitle>
        {acc?.status === 'connected' ? (
          <Badge $tone="success">подключён{acc.company_name ? `: ${acc.company_name}` : ''}</Badge>
        ) : acc?.status === 'revoked' ? (
          <Badge $tone="danger">доступ отозван</Badge>
        ) : acc?.app_configured ? (
          <Badge $tone="info">ключи сохранены, не подключён</Badge>
        ) : (
          <Badge $tone="neutral">не подключён</Badge>
        )}
      </KadryCardHead>
      <CardBody>
        {result === 'connected' && (
          <FallbackBanner tone="info" title={`${ui.title} подключён`}>
            {ui.connectedNote}
          </FallbackBanner>
        )}
        {result === 'error' && (
          <FallbackBanner tone="warning" title={`Подключить ${ui.title} не удалось`}>
            {params.get('message') ?? 'Попробуйте ещё раз.'}
          </FallbackBanner>
        )}
        {error ? <KadryErrorBanner error={error} onRefresh={refetch} /> : null}

        <Step>
          <KadryMuted>
            1. Зарегистрируйте приложение на{' '}
            <a href={ui.devUrl} target="_blank" rel="noreferrer">
              {ui.devLabel}
            </a>{' '}
            {ui.appHint}
          </KadryMuted>
          <Line>
            <Code>{target ?? 'не задан публичный адрес ОСНОВЫ (OSNOVA_KADRY_PUBLIC_BASE_URL)'}</Code>
          </Line>
        </Step>

        <Step>
          <KadryMuted>2. Введите ключи приложения (хранятся зашифрованно, обратно не показываются):</KadryMuted>
          <Row onSubmit={saveApp}>
            <Input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={acc?.app_configured ? `${ui.idLabel} задан — ввести новый` : ui.idLabel}
              aria-label={`${ui.idLabel}: ${ui.title}`}
              autoComplete="off"
            />
            <Input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={acc?.app_configured ? `${ui.secretLabel} задан — ввести новый` : ui.secretLabel}
              aria-label={`${ui.secretLabel}: ${ui.title}`}
              autoComplete="new-password"
            />
            <Button type="submit" $size="sm" disabled={secretState.isLoading || (!clientId && !clientSecret)}>
              Сохранить ключи
            </Button>
          </Row>
          <Line>
            <KeyState label={ui.idLabel} meta={idMeta} />
            <KeyState label={ui.secretLabel} meta={secretMeta} />
          </Line>
          {saved && !secretState.error && (
            <FallbackBanner tone="info" title="Ключи сохранены">
              {acc?.app_configured
                ? `Теперь шаг 3 — «Подключить ${ui.title}».`
                : `Для подключения нужны оба ключа: ${ui.idLabel} и ${ui.secretLabel}.`}
            </FallbackBanner>
          )}
          {secretState.error ? (
            <KadryErrorBanner error={secretState.error} onDismiss={() => secretState.reset()} />
          ) : null}
        </Step>

        <Step>
          <KadryMuted>3. Войдите в {ui.title} под работодателем и разрешите доступ:</KadryMuted>
          <Line>
            <Button
              type="button"
              $size="sm"
              disabled={!acc?.app_configured || connectState.isLoading}
              onClick={onConnect}
            >
              {acc?.status === 'connected' ? 'Подключить заново' : `Подключить ${ui.title}`}
            </Button>
            {acc?.status === 'connected' && (
              <>
                <Button
                  type="button"
                  $size="sm"
                  $variant="secondary"
                  disabled={discoverState.isLoading}
                  onClick={() => discover(source)}
                >
                  Обновить возможности и лимиты
                </Button>
                <Button
                  type="button"
                  $size="sm"
                  $variant="ghost"
                  disabled={disconnectState.isLoading}
                  onClick={() => disconnect(source)}
                >
                  Отключить
                </Button>
              </>
            )}
          </Line>
          {acc?.discovered_at && <KadryMuted>Возможности проверены {kadryDateTime(acc.discovered_at)}.</KadryMuted>}
          {source === 'hh' && acc?.status === 'connected' && <TariffsQuotasCard capabilities={acc.capabilities} />}
          {acc?.last_error && <KadryMuted>Последняя ошибка: {acc.last_error}</KadryMuted>}
          {[connectState.error, discoverState.error, disconnectState.error].map((err, i) =>
            err ? <KadryErrorBanner key={i} error={err} /> : null,
          )}
        </Step>
      </CardBody>
    </Card>
  );
}

export { SourceConnectionsCard as SourceCard };
